import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArchiveBox,
  heroArrowPath,
  heroChevronRight,
  heroCpuChip,
  heroDocumentDuplicate,
  heroMagnifyingGlass,
  heroPlus,
  heroSquares2x2,
} from '@ng-icons/heroicons/outline';
import { NgpSelectPortal } from 'ng-primitives/select';
import {
  ToastrService,
  UiBadge,
  UiButton,
  UiConfirmDialogService,
  UiEmptyState,
  UiFilterActions,
  UiFilterBar,
  UiFilterField,
  UiFilterLabel,
  UiFilterSearch,
  UiIconButton,
  UiInput,
  UiPageHeader,
  UiRecordList,
  UiRecordRow,
  UiRecordRowActions,
  UiRecordRowBody,
  UiRecordRowButton,
  UiRecordRowDescription,
  UiRecordRowIcon,
  UiRecordRowMain,
  UiRecordRowMeta,
  UiRecordRowTitle,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
  UiSpinner,
} from '../../../shared/ui';
import { AgentsApiService, type ManagedAgentArchetype } from '../agents-api.service';

type ScopeFilter = 'all' | 'system' | 'business';
type StatusFilter = 'all' | 'active' | 'disabled' | 'deprecated';

const SCOPE_OPTIONS: ReadonlyArray<{ value: ScopeFilter; label: string }> = [
  { value: 'all', label: 'Todas as origens' },
  { value: 'system', label: 'Templates do sistema' },
  { value: 'business', label: 'Do business' },
];

const STATUS_OPTIONS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'Todos os status' },
  { value: 'active', label: 'Ativos' },
  { value: 'disabled', label: 'Arquivados' },
  { value: 'deprecated', label: 'Descontinuados' },
];

@Component({
  selector: 'app-archetypes-list-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIcon,
    NgpSelectPortal,
    UiBadge,
    UiButton,
    UiEmptyState,
    UiFilterActions,
    UiFilterBar,
    UiFilterField,
    UiFilterLabel,
    UiFilterSearch,
    UiIconButton,
    UiInput,
    UiPageHeader,
    UiRecordList,
    UiRecordRow,
    UiRecordRowActions,
    UiRecordRowBody,
    UiRecordRowButton,
    UiRecordRowDescription,
    UiRecordRowIcon,
    UiRecordRowMain,
    UiRecordRowMeta,
    UiRecordRowTitle,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
    UiSpinner,
  ],
  providers: [
    provideIcons({
      heroArchiveBox,
      heroArrowPath,
      heroChevronRight,
      heroCpuChip,
      heroDocumentDuplicate,
      heroMagnifyingGlass,
      heroPlus,
      heroSquares2x2,
    }),
  ],
  templateUrl: './archetypes-list.page.html',
  styleUrl: './archetypes-list.page.css',
})
export class ArchetypesListPage {
  private readonly api = inject(AgentsApiService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly confirm = inject(UiConfirmDialogService);
  private readonly toast = inject(ToastrService);

  readonly loading = signal(false);
  readonly busyId = signal<string | null>(null);
  readonly archetypes = signal<ManagedAgentArchetype[]>([]);
  readonly search = signal('');
  readonly scope = signal<ScopeFilter>('all');
  readonly status = signal<StatusFilter>('active');
  readonly scopeOptions = SCOPE_OPTIONS;
  readonly statusOptions = STATUS_OPTIONS;

  readonly visibleArchetypes = computed(() => {
    const query = normalize(this.search());
    return this.archetypes().filter((archetype) => !query || normalize([
      archetype.name,
      archetype.description,
      archetype.category,
      archetype.key,
    ].filter(Boolean).join(' ')).includes(query));
  });

  readonly scopeLabel = computed(() => SCOPE_OPTIONS.find((item) => item.value === this.scope())?.label ?? 'Todas');
  readonly statusLabel = computed(() => STATUS_OPTIONS.find((item) => item.value === this.status())?.label ?? 'Todos');
  readonly hasFilters = computed(() => Boolean(this.search().trim()) || this.scope() !== 'all' || this.status() !== 'active');

  constructor() {
    this.refresh();
  }

  refresh(): void {
    this.loading.set(true);
    const status = this.status();
    this.api.listManagedArchetypes({
      scope: this.scope(),
      ...(status === 'all' ? {} : { status: [status] }),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.archetypes.set(response.data);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.toast.error('Nao foi possivel carregar os arquetipos.');
      },
    });
  }

  setSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
  }

  setScope(value: unknown): void {
    if (value === 'all' || value === 'system' || value === 'business') {
      this.scope.set(value);
      this.refresh();
    }
  }

  setStatus(value: unknown): void {
    if (value === 'all' || value === 'active' || value === 'disabled' || value === 'deprecated') {
      this.status.set(value);
      this.refresh();
    }
  }

  clearFilters(): void {
    this.search.set('');
    this.scope.set('all');
    this.status.set('active');
    this.refresh();
  }

  open(archetype: ManagedAgentArchetype): void {
    void this.router.navigate(['/agentes/arquetipos', archetype.id]);
  }

  create(): void {
    void this.router.navigate(['/agentes/arquetipos/novo']);
  }

  duplicate(archetype: ManagedAgentArchetype, event: Event): void {
    event.stopPropagation();
    this.busyId.set(archetype.id);
    this.api.duplicateArchetype(archetype.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (created) => {
        this.busyId.set(null);
        this.toast.success('Template duplicado.');
        void this.router.navigate(['/agentes/arquetipos', created.id]);
      },
      error: () => {
        this.busyId.set(null);
        this.toast.error('Nao foi possivel duplicar o template.');
      },
    });
  }

  async archive(archetype: ManagedAgentArchetype, event: Event): Promise<void> {
    event.stopPropagation();
    const accepted = await this.confirm.confirm({
      title: 'Arquivar arquetipo?',
      description: archetype.agentsUsingCount
        ? `${archetype.agentsUsingCount} agente(s) continuarao usando seus snapshots atuais.`
        : 'O arquetipo deixara de aparecer para novos agentes.',
      confirmLabel: 'Arquivar',
      cancelLabel: 'Cancelar',
      tone: 'warning',
    });
    if (!accepted) return;
    this.busyId.set(archetype.id);
    this.api.archiveArchetype(archetype.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.busyId.set(null);
        this.toast.success('Arquetipo arquivado.');
        this.refresh();
      },
      error: () => {
        this.busyId.set(null);
        this.toast.error('Nao foi possivel arquivar o arquetipo.');
      },
    });
  }

  versionLabel(archetype: ManagedAgentArchetype): string {
    if (archetype.draftVersion) return `v${archetype.draftVersion.version} em rascunho`;
    if (archetype.currentPublishedVersion) return `v${archetype.currentPublishedVersion.version} publicada`;
    return 'Sem versao publicada';
  }

  updatedLabel(value: string): string {
    return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'medium' }).format(new Date(value));
  }
}

function normalize(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR').trim();
}
